<template lang="html">
  <div class="tailor-container">
    <div class="tailor-header">
      <div><h1 class="page-title">Capsule Builder</h1></div>
      <div>
        <b-button
          size="md"
          variant="unstitched"
          class="create-capsule-btn mb-5"
          @click="submitCreateCapsule"
          :disabled="creatingCapsule"
        >
          Create capsule
          <b-spinner small class="ml-1" v-if="creatingCapsule"></b-spinner>
        </b-button>
      </div>
    </div>

    <hr class="mb-4" v-if="!loadingData" />

    <div class="filter-badges mb-4" v-if="!loadingData">
      <b-badge
        pill
        class="mr-1 filter-badge"
        :variant="badge.variant"
        :disabled="filterStatus !== badge.value"
        @click="setFilterStatus(badge.value)"
        v-for="(badge, index) in badges"
        :key="index"
      >
        {{badge.value}}
        <b-badge
          class="ml-2 badge-count"
          variant="light"
          v-html="capsuleCounter(badge.value)"
        />
      </b-badge>
    </div>

    <b-row class="capsule-row pt-3" v-if="isStylist">
      <b-col>
        <PublishCapsuleModal />
        <DestroyCapsuleModal />

        <div class="text-center" v-if="loadingData">
          <b-spinner variant="accent" label="Text Centered"></b-spinner>
        </div>

        <transition-group name="list-capsules" class="list-capsules" tag="div" v-else-if="hasCapsules">
          <div
            v-for="capsule in sortedCapsules"
            :key="capsule.id"
            class="list-capsules-item"
          >
            <b-row class="capsule-row">
              <b-col cols="12" md="8" order="2" order-sm="2" order-md="1">
                <div class="capsule-container" v-bind:class="stateName(capsule)">
                  <CapsuleEmptyStateContent
                    v-if="emptyCapsule(capsule)"
                    :capsule="capsule"
                  />
                  <CapsuleContent :capsule="capsule" v-else />
                </div>
              </b-col>

              <b-col cols="12" md="4" order="1" order-sm="1" order-md="2">
                <CapsuleDetails :capsule="capsule" />
              </b-col>
            </b-row>
          </div>
        </transition-group>

        <b-row class="capsule-row" v-else>
          <TailorEmptyStateContent />
        </b-row>
      </b-col>
    </b-row>

    <b-row class="capsule-row" v-else>
      <TailorMemberEmptyStateContent />
    </b-row>
  </div>
</template>

<script>
// Modals
import DestroyCapsuleModal from "@/components/tailor/modals/DestroyCapsuleModal";
import PublishCapsuleModal from "@/components/capsuleBuilder/modals/PublishCapsuleModal";

// UI Components
import CapsuleContent from "@/components/capsule/CapsuleContent";
import CapsuleDetails from "@/components/tailor/CapsuleDetails";
import CapsuleEmptyStateContent from "@/components/capsule/CapsuleEmptyStateContent";
import TailorEmptyStateContent from "@/components/tailor/TailorEmptyStateContent";
import TailorMemberEmptyStateContent from "@/components/tailor/TailorMemberEmptyStateContent";

// Other
import CapsuleUtils from "@/utils/CapsuleUtils";
import { mapActions, mapGetters } from "vuex";
import { bus } from "@/main";

export default {
  created() {
    this.getStateName = CapsuleUtils.getStateName;
    this.resetAllCapsules();

    if (this.currentUser) {
      const payload = {
        stylistId: this.currentUser.id,
        page: 1,
        per_page: 100,
      };
      this.fetchCapsules(payload).then((response) => {
        this.loadingData = false;
      });
    }
  },

  mounted() {
    window.analytics.page("Tailor");
    this.creatingCapsule = false;
    bus.$on("requestCreateCapsule", () => this.submitCreateCapsule());
  },

  data() {
    return {
      loadingData: true,
      filterStatus: "all",
      creatingCapsule: false,
      badges: [
        { variant: "unstitched", value: "all" },
        { variant: "danger", value: "draft" },
        { variant: "success", value: "ready" },
        { variant: "accent", value: "published" },
      ],
    };
  },

  components: {
    CapsuleContent,
    CapsuleDetails,
    CapsuleEmptyStateContent,
    PublishCapsuleModal,
    DestroyCapsuleModal,
    TailorEmptyStateContent,
    TailorMemberEmptyStateContent,
  },

  computed: {
    ...mapGetters(["allCapsules", "currentUser"]),

    sortedCapsules() {
      if (this.filterCapsules(this.filterStatus)) {
        const sortBy =
          this.filterStatus === "published" ? "published_on" : "created_at";

        const list = this.filterCapsules(this.filterStatus).sort(
          function compare(a, b) {
            var dateA = new Date(a[sortBy]);
            var dateB = new Date(b[sortBy]);
            return dateA - dateB;
          }
        );

        return list.reverse();
      }
    },

    hasCapsules() {
      return (
        Array.isArray(this.sortedCapsules) && this.sortedCapsules.length > 0
      );
    },

    isStylist() {
      return this.currentUser.role === "stylist";
    },
  },

  methods: {
    ...mapActions(["fetchCapsules", "resetAllCapsules", "createCapsule"]),

    submitCreateCapsule() {
      this.creatingCapsule = true;
      const payload = { title: null, occasion: null, notes: null, link: null };
      this.createCapsule(payload);
    },

    capsuleCounter(status) {
      return this.filterCapsules(status).length;
    },

    filterCapsules(status) {
      const that = this;
      if (status === "all") {
        return this.allCapsules;
      } else {
        return this.$lodash.filter(this.allCapsules, function (c) {
          return that.stateName(c) === status;
        });
      }
    },

    setFilterStatus(status) {
      this.filterStatus = status;
    },

    stateName(capsule) {
      return this.getStateName(capsule.status, capsule.publishable);
    },

    emptyCapsule(capsule) {
      const obj = this.$lodash.find(capsule.layers, function (layer) {
        return (layer.layer_products_count || 0) > 0;
      });

      if (obj) {
        return false;
      } else {
        return true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_breakpoints.scss";
@import "~@/styles/_colour-palette.scss";

.tailor-container {
  .tailor-header {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @include for-tablet-portrait-up {
      flex-direction: row;
    }

    .page-title {
      margin-bottom: 30px;

      @include for-tablet-portrait-up {
        margin-bottom: 50px;
      }
    }
  }

  .filter-badges .badge {
    cursor: pointer;
    transition: all 0.35s ease-in-out;
    text-transform: capitalize;

    &.disabled {
      opacity: 0.4;

      .badge-count {
        display: none;
      }

      &:hover {
        opacity: 1;

        .badge-count {
          display: inherit;
        }
      }
    }

    .filter-badge {
      padding-left: 0.5rem;

      @include for-tablet-portrait-up {
        padding-left: 1rem;
      }
    }
  }
}

.capsule-row {
  margin-bottom: 100px;
}

.capsule-container {
  padding: 15px 20px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  background: $grey6;
  border: 1px solid $grey5;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  @include for-tablet-landscape-up {
    padding: 15px 30px;
  }

  &.published {
    border-top-color: $primary-accent;
  }

  &.ready {
    border-top-color: $emerald;
  }

  &.draft {
    border-top-color: $alizarin;
  }
}

.create-capsule-btn {
  border-radius: 0;
  padding: 15px;
  font-size: 11px;
  letter-spacing: 1.4px;

  &:hover {
    box-shadow: 0 8px 24px rgba($primary-blue, 0.25);
  }
}

.list-capsules {
  display: flex;
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
}
.list-capsules-item {
  transition: transform 1s;
  flex: 1 1 100%;
}

.list-capsules-enter, .list-capsules-leave-to
/* .list-capsules-leave-active for <2.1.8 */ {
  opacity: 0;
  transform: translateY(30px);
}
.list-capsules-leave-active {
  position: absolute;
}
</style>



// WEBPACK FOOTER //
// src/pages/Users/Tailor.vue